import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  BarChart3, 
  CreditCard, 
  Building, 
  DollarSign, 
  Settings, 
  LogOut, 
  Mail, 
  Info, 
  Loader2 
} from "lucide-react";
import { Footer } from "../components/layout/Footer";
import { API } from "../lib/api";

export default function HostDashboardPage() {
  const [activeTab, setActiveTab] = useState("overview");
  const [host, setHost] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [upsellPrices, setUpsellPrices] = useState([]);
  const [propertySettings, setPropertySettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('ospitai-host-token');
    if (!token) {
      navigate('/host-login');
      return;
    }
    fetchDashboardData(token);
  }, [navigate]);

  const fetchDashboardData = async (token) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [hostRes, analyticsRes, bookingsRes, pricesRes, propertyRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { headers }),
        axios.get(`${API}/host/analytics`, { headers }),
        axios.get(`${API}/host/bookings`, { headers }),
        axios.get(`${API}/host/upsell-prices`, { headers }),
        axios.get(`${API}/host/property`, { headers })
      ]);
      
      setHost(hostRes.data);
      setAnalytics(analyticsRes.data);
      setBookings(bookingsRes.data);
      setUpsellPrices(pricesRes.data);
      setPropertySettings(propertyRes.data);
    } catch (error) {
      console.error("Dashboard error:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem('ospitai-host-token');
        navigate('/host-login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('ospitai-host-token');
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) {}
    localStorage.removeItem('ospitai-host-token');
    localStorage.removeItem('ospitai-host');
    navigate('/host-login');
  };

  const updateProperty = async (field, value) => {
    setSaving(true);
    const token = localStorage.getItem('ospitai-host-token');
    try {
      await axios.put(`${API}/host/property`, { [field]: value }, { headers: { Authorization: `Bearer ${token}` } });
      setPropertySettings({ ...propertySettings, [field]: value });
    } catch (e) {
      console.error("Update error:", e);
    } finally {
      setSaving(false);
    }
  };

  const updateUpsellPrice = async (packageId, price, isActive) => {
    const token = localStorage.getItem('ospitai-host-token');
    try {
      await axios.put(`${API}/host/upsell-prices`, { package_id: packageId, price, is_active: isActive }, { headers: { Authorization: `Bearer ${token}` } });
      fetchDashboardData(token);
    } catch (e) {
      console.error("Price update error:", e);
    }
  };

  const resendVerification = async () => {
    const token = localStorage.getItem('ospitai-host-token');
    try {
      await axios.post(`${API}/auth/resend-verification`, {}, { headers: { Authorization: `Bearer ${token}` } });
      alert("Verification email sent! Check your inbox.");
    } catch (e) {
      console.error("Resend error:", e);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Loader2 size={32} className="text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="host-dashboard-page">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-stone-900">Host Dashboard</h1>
            <p className="text-sm text-stone-500">{host?.property_name}</p>
          </div>
          <div className="flex items-center gap-4">
            {!host?.is_verified && (
              <button
                onClick={resendVerification}
                className="text-amber-600 text-sm flex items-center gap-1 hover:text-amber-700"
              >
                <Mail size={14} /> Verify email
              </button>
            )}
            <button onClick={handleLogout} className="text-stone-500 hover:text-stone-700 flex items-center gap-1 text-sm">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Verification Banner */}
      {!host?.is_verified && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-3">
          <div className="max-w-6xl mx-auto flex items-center gap-2 text-amber-800 text-sm">
            <Info size={16} />
            <span>Please verify your email to unlock all features.</span>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto">
          {[
            { id: "overview", label: "Overview", icon: BarChart3 },
            { id: "bookings", label: "Bookings", icon: CreditCard },
            { id: "property", label: "Property", icon: Building },
            { id: "pricing", label: "Pricing", icon: DollarSign },
            { id: "settings", label: "Settings", icon: Settings }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? "bg-teal-700 text-white"
                    : "bg-white border border-stone-200 text-stone-600 hover:border-teal-400"
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-6 border border-stone-100">
                <p className="text-sm text-stone-500">Total Bookings</p>
                <p className="text-3xl font-bold text-stone-900 mt-1">{analytics?.total_bookings || 0}</p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-stone-100">
                <p className="text-sm text-stone-500">Total Revenue</p>
                <p className="text-3xl font-bold text-teal-700 mt-1">€{analytics?.total_revenue?.toFixed(2) || "0.00"}</p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-stone-100">
                <p className="text-sm text-stone-500">Chat Interactions</p>
                <p className="text-3xl font-bold text-stone-900 mt-1">{analytics?.chat_interactions || 0}</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 border border-stone-100">
              <h3 className="font-semibold text-stone-900 mb-4">Recent Bookings</h3>
              {bookings.length === 0 ? (
                <p className="text-stone-500 text-sm">No bookings yet.</p>
              ) : (
                <div className="space-y-3">
                  {bookings.slice(0, 5).map((booking) => (
                    <div key={booking.session_id} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                      <div>
                        <p className="font-medium text-stone-800">{booking.package_name}</p>
                        <p className="text-xs text-stone-500">{booking.guest_name || "Guest"} · {new Date(booking.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-teal-700">€{booking.amount}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${booking.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                          {booking.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === "bookings" && (
          <div className="bg-white rounded-xl border border-stone-100 overflow-hidden">
            <div className="p-6 border-b border-stone-100">
              <h3 className="font-semibold text-stone-900">All Bookings</h3>
            </div>
            {bookings.length === 0 ? (
              <div className="p-6 text-center text-stone-500">No bookings yet.</div>
            ) : (
              <div className="divide-y divide-stone-100">
                {bookings.map((booking) => (
                  <div key={booking.session_id} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-stone-800">{booking.package_name}</p>
                      <p className="text-sm text-stone-500">{booking.guest_email || booking.guest_name || "Guest"}</p>
                      <p className="text-xs text-stone-400">{new Date(booking.created_at).toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-teal-700">€{booking.amount}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${booking.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                        {booking.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Property Tab */}
        {activeTab === "property" && (
          <div className="bg-white rounded-xl p-6 border border-stone-100 space-y-6">
            <h3 className="font-semibold text-stone-900">Property Settings</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Property Name</label>
                <input
                  type="text"
                  value={propertySettings.property_name || ""}
                  onChange={(e) => setPropertySettings({ ...propertySettings, property_name: e.target.value })}
                  onBlur={(e) => updateProperty("property_name", e.target.value)}
                  className="w-full border border-stone-200 rounded-lg px-4 py-2 focus:outline-none focus:border-teal-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Location</label>
                <input
                  type="text"
                  value={propertySettings.location || ""}
                  onChange={(e) => setPropertySettings({ ...propertySettings, location: e.target.value })}
                  onBlur={(e) => updateProperty("location", e.target.value)}
                  className="w-full border border-stone-200 rounded-lg px-4 py-2 focus:outline-none focus:border-teal-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Check-in Time</label>
                <input
                  type="time"
                  value={propertySettings.check_in_time || "15:00"}
                  onChange={(e) => updateProperty("check_in_time", e.target.value)}
                  className="w-full border border-stone-200 rounded-lg px-4 py-2 focus:outline-none focus:border-teal-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Check-out Time</label>
                <input
                  type="time"
                  value={propertySettings.check_out_time || "10:00"}
                  onChange={(e) => updateProperty("check_out_time", e.target.value)}
                  className="w-full border border-stone-200 rounded-lg px-4 py-2 focus:outline-none focus:border-teal-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Host Phone</label>
                <input
                  type="tel"
                  value={propertySettings.host_phone || ""}
                  onChange={(e) => setPropertySettings({ ...propertySettings, host_phone: e.target.value })}
                  onBlur={(e) => updateProperty("host_phone", e.target.value)}
                  className="w-full border border-stone-200 rounded-lg px-4 py-2 focus:outline-none focus:border-teal-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">WhatsApp Number</label>
                <input
                  type="tel"
                  value={propertySettings.host_whatsapp || ""}
                  onChange={(e) => setPropertySettings({ ...propertySettings, host_whatsapp: e.target.value })}
                  onBlur={(e) => updateProperty("host_whatsapp", e.target.value)}
                  className="w-full border border-stone-200 rounded-lg px-4 py-2 focus:outline-none focus:border-teal-400"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Address</label>
              <textarea
                value={propertySettings.address || ""}
                onChange={(e) => setPropertySettings({ ...propertySettings, address: e.target.value })}
                onBlur={(e) => updateProperty("address", e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-4 py-2 focus:outline-none focus:border-teal-400"
                rows={2}
              />
            </div>
            
            {saving && <p className="text-sm text-teal-600">Saving...</p>}
          </div>
        )}

        {/* Pricing Tab */}
        {activeTab === "pricing" && (
          <div className="bg-white rounded-xl p-6 border border-stone-100 space-y-4">
            <h3 className="font-semibold text-stone-900">Upsell Pricing</h3>
            <p className="text-sm text-stone-500">Customize prices for your property. Leave blank to use default pricing.</p>
            
            <div className="space-y-4 mt-6">
              {upsellPrices.map((item) => (
                <div key={item.package_id} className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
                  <div>
                    <p className="font-medium text-stone-800">{item.name}</p>
                    <p className="text-sm text-stone-500">Default: €{item.default_price}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-stone-500">€</span>
                      <input
                        type="number"
                        step="0.01"
                        placeholder={item.default_price}
                        defaultValue={item.custom_price || ""}
                        onBlur={(e) => updateUpsellPrice(item.package_id, parseFloat(e.target.value) || item.default_price, item.is_active)}
                        className="w-24 border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:border-teal-400"
                      />
                    </div>
                    <button
                      onClick={() => updateUpsellPrice(item.package_id, item.custom_price || item.default_price, !item.is_active)}
                      className={`px-3 py-1 text-xs font-medium rounded-full ${item.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-200 text-stone-500'}`}
                    >
                      {item.is_active ? "Active" : "Inactive"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-stone-100">
              <h3 className="font-semibold text-stone-900 mb-4">Account</h3>
              <div className="space-y-3">
                <p className="text-sm"><span className="text-stone-500">Email:</span> {host?.email}</p>
                <p className="text-sm">
                  <span className="text-stone-500">Status:</span>{" "}
                  {host?.is_verified ? (
                    <span className="text-emerald-600">Verified</span>
                  ) : (
                    <span className="text-amber-600">Unverified</span>
                  )}
                </p>
                <p className="text-sm"><span className="text-stone-500">Member since:</span> {new Date(host?.created_at).toLocaleDateString()}</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 border border-stone-100">
              <h3 className="font-semibold text-stone-900 mb-4">Data & Privacy</h3>
              <p className="text-sm text-stone-500 mb-4">
                You can request a copy of your data or request deletion at any time.
              </p>
              <div className="flex gap-3">
                <a 
                  href={`${API}/gdpr/my-data`}
                  target="_blank"
                  rel="noreferrer"
                  className="px-4 py-2 text-sm font-medium text-teal-700 border border-teal-200 rounded-lg hover:bg-teal-50"
                >
                  Export my data
                </a>
                <Link 
                  to="/privacy"
                  className="px-4 py-2 text-sm font-medium text-stone-600 border border-stone-200 rounded-lg hover:bg-stone-50"
                >
                  Privacy Policy
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
